import React, { useEffect, useMemo, useState, useCallback } from "react";
import { geoMercator } from "d3-geo";
import { geoPath } from "d3-geo";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Warehouse, Wrench, X, RefreshCw, Truck, TriangleAlert, Boxes, ArrowRight } from "lucide-react";
import zaGeo from "@/assets/za-districts.json";

const W = 900, H = 780;

const COLORS = {
  healthy: "#22c55e",
  low: "#ef4444",
  norule: "#64748b",
  internal: "#00c9b7",
  transit: "#38bdf8",
  amber: "#f59e0b",
  backbone: "#1e3a5f",
};

const curve = (a, b, k = 0.22) => {
  const [x1, y1] = a, [x2, y2] = b;
  const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const cx = mx + (-dy / len) * len * k;
  const cy = my + (dx / len) * len * k;
  return `M${x1},${y1} Q${cx},${cy} ${x2},${y2}`;
};
const curveMid = (a, b, k = 0.22) => {
  const [x1, y1] = a, [x2, y2] = b;
  const dx = x2 - x1, dy = y2 - y1;
  const cx = (x1 + x2) / 2 - dy * k, cy = (y1 + y2) / 2 + dx * k;
  return [0.25 * x1 + 0.5 * cx + 0.25 * x2, 0.25 * y1 + 0.5 * cy + 0.25 * y2];
};

export default function StockMap() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [hover, setHover] = useState(null);
  const [loading, setLoading] = useState(false);

  const canRaise = ["fitment_technician", "operations_controller", "regional_manager", "system_admin"].includes(user.role);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/map").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const { projection, pathGen } = useMemo(() => {
    const proj = geoMercator().fitExtent([[24, 20], [W - 24, H - 90]], zaGeo);
    return { projection: proj, pathGen: geoPath(proj) };
  }, []);

  const nodes = useMemo(() => data?.nodes || [], [data]);
  const nodeById = useMemo(() => Object.fromEntries(nodes.map((n) => [n.id, n])), [nodes]);
  const pts = useMemo(() => {
    const m = {};
    nodes.forEach((n) => { const p = projection([n.lng, n.lat]); if (p) m[n.id] = p; });
    return m;
  }, [nodes, projection]);

  const health = (n) => (n.type === "main_bin" || n.type === "regional") ? "internal"
    : !n.has_rule ? "norule" : n.below_min ? "low" : "healthy";

  const selNode = sel ? nodeById[sel] : null;

  const navigate = useNavigate();
  const topUps = (n) => n.products.filter((p) => p.below).map((p) => ({ ...p, qty: p.max - p.onhand - (p.incoming || 0) })).filter((p) => p.qty > 0);

  const raiseReplenishment = async (n) => {
    const below = topUps(n);
    if (below.length === 0) { toast.info("Stock already in transit covers this site up to max — nothing to raise"); return; }
    try {
      for (const p of below) {
        await api.post("/replenishment", { site_id: n.id, product_id: p.product_id, qty: p.qty });
      }
      toast.success(`Replenishment raised for ${below.length} product(s) at ${n.name}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not raise replenishment"); }
  };

  return (
    <div data-testid="stock-map-page" className="-m-8 p-8 min-h-[calc(100vh-0px)]" style={{ background: "#07121f" }}>
      <style>{`
        @keyframes flow { to { stroke-dashoffset: -40; } }
        @keyframes pulsehalo { 0% { r: 10; opacity: .55; } 70% { r: 30; opacity: 0; } 100% { opacity: 0; } }
        .flowline { stroke-dasharray: 5 9; animation: flow 1s linear infinite; }
        .flowline.slow { animation-duration: 1.6s; }
        .pin { cursor: pointer; transition: transform .15s ease; }
        .pin:hover { transform: translateY(-2px); }
      `}</style>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="overline text-[#00c9b7]">Consignment Platform</div>
          <h1 className="font-head text-2xl font-bold text-white">Live Stock Network Map</h1>
          <p className="text-sm text-slate-400 mt-0.5">Where stock sits, what's moving, and which sites need replenishment — across South Africa.</p>
        </div>
        <Button data-testid="map-refresh" onClick={load} variant="outline" className="rounded-sm border-white/15 bg-white/5 text-slate-200 hover:bg-white/10 font-head">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          {[
            { k: "sites", label: "Sites", icon: Boxes, c: "#00c9b7" },
            { k: "internal", label: "Internal", icon: Warehouse, c: "#38bdf8" },
            { k: "external", label: "External FMCs", icon: Wrench, c: "#a78bfa" },
            { k: "in_transit", label: "In transit", icon: Truck, c: "#38bdf8" },
            { k: "below_min", label: "Below minimum", icon: TriangleAlert, c: "#ef4444" },
          ].map(({ k, label, icon: Icon, c }) => (
            <div key={k} data-testid={`map-stat-${k}`} className="rounded-sm border border-white/10 bg-white/[0.03] px-4 py-3" style={{ borderLeft: `3px solid ${c}` }}>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-400"><Icon className="h-3.5 w-3.5" style={{ color: c }} /> {label}</div>
              <div className="font-head text-3xl font-bold mt-1" style={{ color: c }}>{data.totals[k]}</div>
            </div>
          ))}
        </div>
      )}

      <div className="relative rounded-lg overflow-hidden border border-white/10"
        style={{ background: "radial-gradient(1200px 700px at 60% 15%, #0f2d4a 0%, #0a1b2e 55%, #07121f 100%)" }}>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block" data-testid="stock-map-svg">
          <defs>
            <filter id="glow" x="-80%" y="-80%" width="260%" height="260%">
              <feGaussianBlur stdDeviation="4" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>

          {/* landmass */}
          {zaGeo.features.map((f, i) => (
            <path key={i} d={pathGen(f)} fill="#0e2338" stroke="#1b3c5c" strokeWidth={0.6} />
          ))}

          {/* links */}
          <g strokeLinecap="round" fill="none">
            {(data?.links || []).map((l, i) => {
              const a = pts[l.from], b = pts[l.to];
              if (!a || !b) return null;
              const d = curve(a, b);
              if (l.kind === "backbone") return <path key={i} d={d} stroke={COLORS.backbone} strokeWidth={1} opacity={0.5} />;
              const color = l.kind === "low" ? COLORS.low : l.kind === "transit_low" ? COLORS.amber : COLORS.transit;
              return (
                <g key={i}>
                  <path d={d} stroke={color} strokeWidth={3.5} opacity={0.18} />
                  <path d={d} stroke={color} strokeWidth={2} className={`flowline ${l.kind === "low" ? "slow" : ""}`}
                    opacity={0.95} style={l.kind === "low" ? { animationDirection: "reverse" } : undefined} />
                  {l.kind !== "low" && (
                    <circle r={3.2} fill="#ffffff" filter="url(#glow)">
                      <animateMotion dur="3s" repeatCount="indefinite" path={d} />
                    </circle>
                  )}
                </g>
              );
            })}
          </g>

          {/* transit qty badges */}
          {(data?.links || []).filter((l) => l.kind.startsWith("transit") && l.count > 0).map((l, i) => {
            const a = pts[l.from], b = pts[l.to];
            if (!a || !b) return null;
            const [mx, my] = curveMid(a, b);
            const color = l.kind === "low" ? COLORS.low : l.kind === "transit_low" ? COLORS.amber : COLORS.transit;
            const label = l.kind === "low" ? `${l.count} short` : `${l.count} in transit`;
            const w = label.length * 5.6 + 14;
            return (
              <g key={`b${i}`} transform={`translate(${mx},${my})`} data-testid={`map-link-badge-${l.to}`} style={{ pointerEvents: "none" }}>
                <rect x={-w / 2} y={-9} width={w} height={18} rx={9} fill="#07121f" stroke={color} strokeWidth={1.2} opacity={0.92} />
                <text y={3.5} textAnchor="middle" fontSize={10} fontWeight="700" fill={color}>{label}</text>
              </g>
            );
          })}

          {/* pins */}
          {nodes.map((n) => {
            const p = pts[n.id]; if (!p) return null;
            const [x, y] = p;
            const h = health(n);
            const isWH = n.type === "main_bin" || n.type === "regional";
            const col = h === "internal" ? COLORS.internal : h === "healthy" ? COLORS.healthy : h === "low" ? COLORS.low : COLORS.norule;
            const active = sel === n.id || hover === n.id;
            return (
              <g key={n.id} transform={`translate(${x},${y})`}><g className="pin"
                onMouseEnter={() => setHover(n.id)} onMouseLeave={() => setHover(null)}
                onClick={() => setSel(n.id)} data-testid={`map-pin-${n.id}`}>
                {n.below_min && <circle r={10} fill={COLORS.low}><animate attributeName="r" values="10;28" dur="1.8s" repeatCount="indefinite" /><animate attributeName="opacity" values="0.5;0" dur="1.8s" repeatCount="indefinite" /></circle>}
                {active && <circle r={isWH ? 20 : 16} fill="none" stroke={col} strokeWidth={1.5} opacity={0.6} />}
                {isWH ? (
                  <rect x={-10} y={-10} width={20} height={20} rx={4} fill={col} stroke="#eafffb" strokeWidth={1.5} filter="url(#glow)"
                    opacity={n.type === "main_bin" ? 1 : 0.92} />
                ) : (
                  <circle r={n.onhand > 0 ? 9 : 7} fill={col} filter="url(#glow)"
                    stroke={n.ownership === "external" ? "#fde68a" : "#ffffff"} strokeWidth={1.6}
                    strokeDasharray={n.ownership === "external" ? "3 2" : undefined} />
                )}
                {n.onhand > 0 && <text y={isWH ? 4 : 3.5} textAnchor="middle" fontSize={isWH ? 10 : 8.5} fontWeight="700" fill="#06202e">{n.onhand}</text>}
                {(isWH || active) && (
                  <text y={isWH ? -16 : -14} textAnchor="middle" fontSize={10} fontWeight="600"
                    fill={active ? "#ffffff" : "#cbd5e1"} style={{ paintOrder: "stroke", stroke: "#07121f", strokeWidth: 3 }}>
                    {n.name}
                  </text>
                )}
              </g></g>
            );
          })}
        </svg>

        {/* legend */}
        <div className="absolute bottom-3 left-3 bg-black/40 backdrop-blur-md rounded-sm border border-white/10 px-3 py-2.5 text-[11px] text-slate-200 space-y-1.5" data-testid="map-legend">
          <div className="flex items-center gap-2"><span className="inline-block w-4 h-[3px] rounded" style={{ background: COLORS.transit }} /> In transit (healthy)</div>
          <div className="flex items-center gap-2"><span className="inline-block w-4 h-[3px] rounded" style={{ background: COLORS.amber }} /> In transit · still below min</div>
          <div className="flex items-center gap-2"><span className="inline-block w-4 h-[3px] rounded" style={{ background: COLORS.low }} /> Needs replenishment</div>
          <div className="h-px bg-white/10 my-1" />
          <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-[3px]" style={{ background: COLORS.internal }} /> Warehouse (internal)</div>
          <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-full border border-yellow-200 border-dashed" style={{ background: COLORS.healthy }} /> External FMC (healthy)</div>
          <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-full border border-yellow-200 border-dashed" style={{ background: COLORS.low }} /> External FMC (low)</div>
        </div>
      </div>

      {/* drill-down panel */}
      {selNode && (
        <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-[#0a1726] border-l border-white/10 shadow-2xl overflow-y-auto" data-testid="map-drill-panel">
          <div className="sticky top-0 bg-[#0a1726] border-b border-white/10 px-5 py-4 flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                {(selNode.type === "main_bin" || selNode.type === "regional")
                  ? <Warehouse className="h-4 w-4 text-[#00c9b7]" /> : <Wrench className="h-4 w-4 text-violet-300" />}
                <span className="text-[11px] uppercase tracking-wide text-slate-400">
                  {selNode.type.replace(/_/g, " ")} · {selNode.ownership}
                </span>
              </div>
              <h2 className="font-head text-lg font-bold text-white mt-1">{selNode.name}</h2>
              <div className="text-xs text-slate-400">{selNode.area || selNode.region}</div>
            </div>
            <button data-testid="map-drill-close" onClick={() => setSel(null)} className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
          </div>

          <div className="p-5 space-y-5">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-sm bg-white/[0.04] border border-white/10 p-3 text-center">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">On hand</div>
                <div className="font-head text-2xl font-bold text-[#00c9b7]">{selNode.onhand}</div>
              </div>
              <div className="rounded-sm bg-white/[0.04] border border-white/10 p-3 text-center">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">In transit</div>
                <div className="font-head text-2xl font-bold text-[#38bdf8]">{selNode.incoming}</div>
              </div>
              <div className="rounded-sm bg-white/[0.04] border border-white/10 p-3 text-center">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Status</div>
                <div className={`font-head text-sm font-bold mt-1.5 ${selNode.below_min ? "text-[#ef4444]" : selNode.has_rule ? "text-[#22c55e]" : "text-slate-400"}`}>
                  {selNode.below_min ? "BELOW MIN" : selNode.has_rule ? "HEALTHY" : "NO RULE"}
                </div>
              </div>
            </div>

            {selNode.products.length > 0 ? (
              <div>
                <div className="overline text-slate-400 mb-2">Stock by product (on-hand vs min/max)</div>
                <div className="space-y-3">
                  {selNode.products.map((p) => {
                    const pct = Math.min(100, Math.round((p.onhand / Math.max(1, p.max)) * 100));
                    return (
                      <div key={p.product_id} className="rounded-sm bg-white/[0.03] border border-white/10 p-3" data-testid={`map-product-${p.product_id}`}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-200 font-medium">{p.product_name}</span>
                          <span className={p.below ? "text-[#ef4444] font-bold" : "text-[#22c55e] font-bold"}>{p.onhand} / min {p.min}</span>
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden relative">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: p.below ? COLORS.low : COLORS.healthy }} />
                          <div className="absolute top-0 bottom-0" style={{ left: `${Math.min(100, (p.min / Math.max(1, p.max)) * 100)}%`, width: 2, background: "#fbbf24" }} />
                        </div>
                        <div className="flex items-center justify-between text-[10px] text-slate-500 mt-1">
                          <span>max {p.max}{p.incoming ? ` · ${p.incoming} inbound` : ""}</span>
                          <span>min marker</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-500 rounded-sm bg-white/[0.03] border border-white/10 p-3">No min/max stock rule set for this site.</div>
            )}

            {(selNode.address || selNode.contact_name || selNode.telephones) && (
              <div>
                <div className="overline text-slate-400 mb-2">Site details</div>
                <div className="text-sm text-slate-300 space-y-1">
                  {selNode.contact_name && <div><span className="text-slate-500">Contact:</span> {selNode.contact_name}</div>}
                  {selNode.telephones && <div><span className="text-slate-500">Tel:</span> {selNode.telephones}</div>}
                  {selNode.address && <div><span className="text-slate-500">Address:</span> {selNode.address}</div>}
                  {selNode.grade && <div><span className="text-slate-500">Grade:</span> {selNode.grade}</div>}
                  <div className="text-[11px] text-slate-500 pt-1">Pin placed by: {selNode.geo_resolved === "exact" ? "exact coordinates" : selNode.geo_resolved === "city" ? "city/town" : selNode.geo_resolved}</div>
                </div>
              </div>
            )}

            {(selNode.open_pending > 0 || selNode.open_dispatched > 0) && (
              <div className="space-y-2" data-testid="map-open-requests">
                <div className="text-xs text-slate-400">
                  {selNode.open_pending > 0 && <span className="text-[#fbbf24] font-semibold">{selNode.open_pending} awaiting fulfilment</span>}
                  {selNode.open_pending > 0 && selNode.open_dispatched > 0 && " · "}
                  {selNode.open_dispatched > 0 && <span className="text-[#38bdf8] font-semibold">{selNode.open_dispatched} dispatched, awaiting receipt</span>}
                </div>
                <Button data-testid="map-open-in-replenishment" onClick={() => navigate(`/replenishment?site=${selNode.id}`)}
                  className="w-full rounded-sm bg-[#00c9b7] hover:bg-[#00b3a3] text-[#06202e] font-head">
                  {selNode.open_pending > 0 ? "Fulfil in Replenishment" : "View in Replenishment"} <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}
            {canRaise && selNode.below_min && selNode.open_pending === 0 && selNode.open_dispatched === 0 && (
              topUps(selNode).length > 0 ? (
                <Button data-testid="map-raise-replenishment" onClick={() => raiseReplenishment(selNode)}
                  className="w-full rounded-sm bg-[#ef4444] hover:bg-[#dc2626] font-head">
                  <Truck className="h-4 w-4 mr-2" /> Raise replenishment ({topUps(selNode).reduce((a, p) => a + p.qty, 0)} units to max)
                </Button>
              ) : (
                <div data-testid="map-covered-by-transit" className="text-xs text-slate-400 rounded-sm bg-white/[0.03] border border-white/10 p-3">In-transit stock already covers this site up to max.</div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
